import "@/App.css";
import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";

const Landing = lazy(() => import("@/pages/Landing"));
const About = lazy(() => import("@/pages/About"));
const EventDetail = lazy(() => import("@/pages/EventDetail"));
const EventRedirect = lazy(() => import("@/pages/EventRedirect"));
const Auth = lazy(() => import("@/pages/Auth"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const OrganiserDashboard = lazy(() => import("@/pages/OrganizerDashboard"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const AdminBuzzboard = lazy(() => import("@/pages/AdminBuzzboard"));
const BeeDashboard = lazy(() => import("@/pages/BeeDashboard"));
const ReferralLanding = lazy(() => import("@/pages/ReferralLanding"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const Install = lazy(() => import("@/pages/Install"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/Blog").then(m => ({ default: m.BlogPost })));
const HostsDirectory = lazy(() => import("@/pages/HostsDirectory"));
const HostPublicProfile = lazy(() => import("@/pages/HostPublicProfile"));

const PageLoader = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="text-primary text-xl font-semibold animate-pulse">Loading...</div>
  </div>
);

class ChunkErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError(error) {
    if (error?.name === 'ChunkLoadError' || error?.message?.includes('Loading chunk')) {
      return { hasError: true };
    }
    throw error;
  }
  componentDidCatch() { window.location.reload(); }
  render() {
    if (this.state.hasError) return <PageLoader />;
    return this.props.children;
  }
}

const PrivateRoute = ({ children, requireAdmin = false }) => {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/auth" />;
  if (requireAdmin && user.role !== "admin") return <Navigate to="/" />;
  return children;
};

function App() {
  return (
    <div className="App dark">
      <AuthProvider>
        <BrowserRouter>
          <ChunkErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/about" element={<About />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/install" element={<Install />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/events/:id" element={<EventRedirect />} />
              <Route path="/hosts" element={<HostsDirectory />} />
              <Route path="/hosts/:id" element={<HostPublicProfile />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/bee/:referralId" element={<ReferralLanding />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/organiser/*" element={
                <PrivateRoute><OrganiserDashboard /></PrivateRoute>
              } />
              <Route path="/bee-dashboard" element={
                <PrivateRoute><BeeDashboard /></PrivateRoute>
              } />
              <Route path="/admin/*" element={
                <PrivateRoute requireAdmin><AdminDashboard /></PrivateRoute>
              } />
              <Route path="/admin/buzzboard" element={
                <PrivateRoute requireAdmin><AdminBuzzboard /></PrivateRoute>
              } />
              <Route path="/:state/:postcode/:slug" element={<EventDetail />} />
              </Routes>
            </Suspense>
          </ChunkErrorBoundary>
          <PWAInstallPrompt />
        </BrowserRouter>
        <Toaster position="top-right" />
      </AuthProvider>
    </div>
  );
}

export default App;
